import { reducerWithInitialState } from 'typescript-fsa-reducers';
import {
  setCurrentCompetencyId,
  addNewResults,
  startNewClassSession,
  sdSetPollTimeout,
  SdCreatedParams,
  loadAndDisplayExampleFromEventId,
  removeDisplayedExample,
  ShowExamplePayload,
  StudentResultsParams,
  addNewEventForExample,
  addNewEvents,
  exampleToDisplayGenerated,
  generateExampleFromEventId,
  GenerateExamplePayload,
  AddNewEventParams,
  setStudentCurrentComptencyId,
  setModuleCompletionForUser,
  SetModuleCompletionPayload
} from '../actions/statistics';
import {
  RawPageData,
  SkillContent,
  SFFProblemType,
  Tag,
  isVestigialType
} from 'its-pages';
import { TimeoutType } from '../../utils/utils';
import { ReducerFn } from '@lib/utils/redux/reducers/types';
import { RosterUpdatePayload, updateRoster } from '@/redux/actions/firebase-actions';
import { setLTIModule, setURLModule } from '../actions/app-actions';
import { tourConfusionExampleRawPageData, tourIdentifyExampleRawPageData } from '../../utils/const-data';
import { ConfusionDetails, IssueDetails, StudentResult } from '../../types/cards';
import { SavedModuleCompletionInfo } from '../../types/module-completion';

export interface Competency {
  id: string;
  name: string;
  content: SkillContent;
}

export interface Competencies {
  [id: string]: Competency;
}

export type ResultsByStudentLevel = {
  [levelId in SFFProblemType]: RawPageData[];
}

type StatisticsTag = Tag;

interface StatisticsTags {
  [tagId: string]: StatisticsTag;
}

export interface StatisticsTagsByCompetency {
  [competency: string]: StatisticsTags;
}

/** The key is formatted <tag 1 id>-<tag 2 id> */
//type TagConfusions = Record<string, TagConfusion>;

type StatsSource = "loki" | "firebase";

interface StudentStats {
  cumulativeTime: number;
  progress: number;
  problemCount: number;
  /**
   * Temp hack to prevent loki from overwriting stats coming from Firebase.
   * We still want to accept stats coming from loki if Firebase stats are
   * not available for this student.
   **/
  lastSetBy: StatsSource | null;

  /**
   * Student submissions/question results grouped by the question type.
   * (??? Level is misleading term)
   * TODO: Eliminate, this data should be saved elsewhere in a memoized store
   * only on click of the row do we trigger a fetch of user data which can be sorted
   */
  resultsByType: Partial<ResultsByStudentLevel>;
}

/**
 * Mapping from skill/module id to stats for a specific student for that skill/module
 */
interface StudentStatsByCompetency {
  [id: string]: StudentStats;
}

/**
 * Mapping from student id to another mapping from skill/module id to stats
 */
interface StudentsStats {
  [uid: string]: StudentStatsByCompetency;
}

interface UserCardRecord {
  userId: string;
  correct?: boolean;
  level?: number;
}

interface NewIssueCard {
  id: string;
  tag: string;
  skill: string;
  questionType: SFFProblemType;
  // Event id to user id map
  eventIdToUser:Record<string, UserCardRecord>;
}

interface NewConfusionCard {
  id: string;
  tagA: string;
  tagB: string;
  skill: string;
  eventIdToUser:Record<string, UserCardRecord>;
}

export interface StatisticsState {
  sessionId: string;
  pollTimeout?: TimeoutType;
  pollIntervalMs: number;
  lastLogResultTime: number;
  /**
   * Mapping from skill/module id (like Empathy) to details about that skill/module.
   * General description data.
   */
  competencies: Competencies;

  /**
   * Mapping from skill/module (like Empathy) to another mapping from tag id to a Tag object (which is like
   * a component of the skill/module)
   * General description data.
   */
  tagsByCompetency: StatisticsTagsByCompetency;

  /** 
   * The current skill/module being worked on. For example if an assignment is focused on Empathy,
   * this would be set to empathy.
   */
  currentCompetencyId: string;

  /**
   * Set of all skill ids that are present
   */
  skillIdsPresent: Record<string, boolean>;

  /**
   * ???
   */
  initialDataLoaded: boolean;
  /**
   * Should be poll for dashboard updates
   */
  isPollingEnabled: boolean;
  /**
   * Mapping from user id to student play stats (includes data used in teacher dashboard table and
   * user question results). The play stats are aggregated by the skill/module.
   */
  studentsStats: StudentsStats;

  /**
   * Each issue type card should have
   * tag
   * skill
   * questionType
   * eventId to userId map
   */
  cardsIssueType: Record<string, NewIssueCard>;

  /**
   * Each confusion type card should have
   * tagA
   * tagB
   * skill
   * questionType
   * eventId to userId map
   */
  cardsConfusionType: Record<string, NewConfusionCard>;  

  /**
   * Mapping from event id (in firebase this is a document id) to raw event json object.
   * Parse the raw json to be able to recreate a specific problem example.
   */
  eventIdToEvent: Record<string, RawPageData>;

  currentEventIdDisplayed: string | null;

  /**
   * Cuurent question/event displayed in the dashboard
   */
  currentEventDataToDisplay: RawPageData | null;
  currentEventShowAnswers: boolean;
  currentEventShowUser: boolean;
  currentEventTagsIdToEmphasize: string[] | undefined;

  /**
   * The issue card id the user interacted with to open up the event to display
   */
  currentCardId: string | undefined;

  /**
   * If true, the example event to display is loading (note this could also be for generated problems)
   */
  eventToDisplayIsLoading: boolean;

  /**
   * User and Competency to module completion info
   * key: {Module Id} {uid}
   * value: module completion data
   */
  competencyAndUserToCompletionMap: Record<string, SavedModuleCompletionInfo>;
}

const ENABLE_POLLING = false;
const DEFAULT_POLL_INTERVAL_MS = 30_000;

const INITIAL_STATE: StatisticsState = {
  sessionId: '',
  lastLogResultTime: 0,
  competencies: {},
  tagsByCompetency: {},
  currentCompetencyId: '',
  initialDataLoaded: !ENABLE_POLLING,
  isPollingEnabled: ENABLE_POLLING,
  pollIntervalMs: DEFAULT_POLL_INTERVAL_MS,
  studentsStats: {},

  skillIdsPresent: {},
  cardsIssueType: {},
  cardsConfusionType: {},
  // Populate with page data needed for the teacher dashboard intro tour
  eventIdToEvent: {
    tourIdentifyExamplePageData: tourIdentifyExampleRawPageData,
    tourConfusionExamplePageData: tourConfusionExampleRawPageData
  },
  currentEventIdDisplayed: null,
  currentEventDataToDisplay: null,
  currentEventShowAnswers: true,
  currentEventShowUser: false,
  currentEventTagsIdToEmphasize: undefined,
  currentCardId: undefined,
  eventToDisplayIsLoading: false,
  competencyAndUserToCompletionMap: {}
};

type StatsReducer<P> = ReducerFn<StatisticsState, P>;

const setCurrentCompetencyReducer: StatsReducer<string> = (
  state,
  currentCompetencyId
) => ({ ...state, currentCompetencyId });


/**
Fundamental issue:
for dialog type questions, want to know whenever user picks incorrect option for a dialog.
The incorrect user option should be compared with the actual correct one.
This forms a tag mistake pair.

question/problem has options to select, look at the ones that are part of the correct answer.
 * compare it to a different option in the set that is NOT part of the correct answer.
 * get all tags belonging to both options
 * aggregation based on each pair of tags (i.e. tags from the right answers with tags from the wrong answer)
 * 
 * NOTE: This will count up all instances where a pair shows up in addition to counts where player
 * made a mistake with that pair. A mistake we define as the user NOT selecting the correct option
 * AND selecting the incorrect option.
 */

const INITIAL_STUDENT_STATS: StudentStats = {
  cumulativeTime: 0,
  progress: 0,
  problemCount: 0,
  lastSetBy: null,
  // HACK: This object gets shared amongst all students, need to manually create a new object later on
  // if you create a clone of this initial state.
  resultsByType: {},
};

/**
 * Each user/student has a breakdown per skill/module
 * Within the per skill/module data is a collection of the page data and the stats that are displayed in
 * the results table.
 * 
 * @param studentsStats 
 * @param pageData 
 */
function addNewStudentStatResult(studentsStats: StudentsStats, pageData: RawPageData) {
  const { skill, type, playStats } = pageData;
  if (!playStats)
    return
  const { userId } = playStats;
  const studentStatsByCompetency = studentsStats[userId] || {};

  // Add student stats by competency/skill
  const studentStats = studentStatsByCompetency[skill.id] || {
    ...INITIAL_STUDENT_STATS,
    resultsByType: {},
    lastSetBy: 'loki'
  };

  studentStats.progress = studentStats.lastSetBy === 'firebase'
    ? studentStats.progress
    : Math.max(
      pageData.type === SFFProblemType.ModuleCompletion
        ? 100
        : playStats.progress ?
          playStats.progress : 0,
      studentStats.progress
    );

  if (pageData.type !== SFFProblemType.ModuleCompletion) {
    if (studentStats.lastSetBy !== 'firebase') {
      studentStats.problemCount++;
      studentStats.cumulativeTime += playStats.cumulativeTime;
    }
    studentStats.resultsByType = studentStats.resultsByType ?? {};
    const resultsByType = studentStats.resultsByType?.[type] ?? [];
    resultsByType.push(pageData);

    studentStats.resultsByType[type] = resultsByType;
  }
  studentStatsByCompetency[skill.id] = { ...studentStats };

  studentsStats[userId] = studentStatsByCompetency;
}

/**
 * Reducer to handle processing lists of new raw page data (i.e. the events from loki or
 * from firebase)
 * 
 * @param state 
 * @param param1 
 * @returns 
 */
const addNewResultReducer: StatsReducer<StudentResultsParams> = (
  state,
  {
    cardResults, lastLogTime: lastLogResultTime,
    skillDescriptions, skillIdToTags
  }
) => {
  const studentsStats = { ...state.studentsStats };

  if (cardResults) {
    cardResults.forEach((card) => {
      const { details, eventIdToUser, id, skill, type } = card;
      if (type === 'Confusion') {
        const confusionMap = state.cardsConfusionType;
        const confusionDetails = details as ConfusionDetails;
        const mapToSave: Record<string, UserCardRecord> = {};
        for (const eventId in eventIdToUser) {
          const user = eventIdToUser[eventId];
          mapToSave[eventId] = {
            userId: user.uid,
            correct: user.correct,
            level: user.problemLevel,
          }
        }
        confusionMap[id] = {
          eventIdToUser: mapToSave,
          id,
          skill,
          tagA: confusionDetails.tagAId,
          tagB: confusionDetails.tagBId
        }
      } else if (type === 'Issue') {
        const issuesMap = state.cardsIssueType;
        const issueDetails = details as IssueDetails;
        const mapToSave: Record<string, UserCardRecord> = {};
        for (const eventId in eventIdToUser) {
          const user = eventIdToUser[eventId];
          mapToSave[eventId] = {
            userId: user.uid,
            correct: user.correct,
            level: user.problemLevel
          }
        }
        issuesMap[id] = {
          id,
          skill,
          eventIdToUser: mapToSave,
          questionType: issueDetails.questionType as SFFProblemType,
          tag: issueDetails.tagId
        };
      }
      state.skillIdsPresent[skill] = true;
    });
  }
  

  return {
    ...state,
    competencies: (skillDescriptions) ? skillDescriptions : state.competencies,
    tagsByCompetency: (skillIdToTags) ? skillIdToTags : state.tagsByCompetency,
    cardsConfusionType: {...state.cardsConfusionType},
    cardsIssueType: {...state.cardsIssueType},
    skillIdsPresent: {...state.skillIdsPresent},
    studentsStats,
    lastLogResultTime
  };
}

/**
 * This is the start of a new session. Need to reset data
 * @param _state 
 * @param param1 
 * @returns 
 */
const sdCreatedReducer: StatsReducer<SdCreatedParams> = (
  _state,
  { sessionId, logStartTsMs }
) => ({
  ..._state,
  sessionId,
  lastLogResultTime: logStartTsMs,
  cardsConfusionType: {},
  cardsIssueType: {},
  studentsStats: {},
  eventIdToEvent: {
    tourIdentifyExamplePageData: tourIdentifyExampleRawPageData,
    tourConfusionExamplePageData: tourConfusionExampleRawPageData
  },
  currentEventIdDisplayed: null,
  currentEventDataToDisplay: null,
  currentEventShowAnswers: true
});

const sdSetPollTimeoutReducer: StatsReducer<TimeoutType> = (
  state,
  pollTimeout
) => ({
  ...state,
  pollTimeout,
  initialDataLoaded: state.initialDataLoaded || pollTimeout !== undefined,
});

const handleUpdateRoster: StatsReducer<RosterUpdatePayload> = (state, { roster: students }) => {
  const { studentsStats } = state;
  const newStats: StudentsStats = {};
  for (const uid in students) {
    const studentStatsFromPayload = students[uid].stats?.["ITS2021"];
    if (studentStatsFromPayload === undefined) {
      continue;
    }
    /** Code like this is why we want to start using Immer in this project */
    const currentStudentStats = studentsStats[uid] ?? {};
    for (const skill in studentStatsFromPayload) {
      const { problemsDone, progress, cumulativeTime } = studentStatsFromPayload[skill];
      const currentSkillStats = currentStudentStats[skill] ?? {
        ...INITIAL_STUDENT_STATS,
        resultsByType: {},
        lastSetBy: 'firebase'
      };
      currentStudentStats[skill] = {
        ...currentSkillStats,
        problemCount: problemsDone,
        cumulativeTime,
        // HACK: Firebase progress reset to zero if user finished module, we had been relying on loki to
        // reset this state based on module complete event
        progress: (progress === 0 && problemsDone > 16) ? 100 : progress
      };
    }
    newStats[uid] = { ...currentStudentStats };
  }
  return { ...state, studentsStats: { ...studentsStats, ...newStats } };
};

const setLtiModuleReducer: StatsReducer<string> = (state, ltiModuleName: string) => {
  const currentSkillsPresent = state.skillIdsPresent;
  currentSkillsPresent[ltiModuleName] = true;
  return {
    ...state,
    skillIdsPresent: { ...currentSkillsPresent },
    currentCompetencyId: ltiModuleName
  };
};

const loadAndDisplayExampleEventReducer: StatsReducer<ShowExamplePayload> = (
  state, { eventId, showAnswers, showUsername, tagIdsToEmphasize, cardId }) => {
  const existingPageData = state.eventIdToEvent[eventId];

  // If the card id has NOT changed, do not overwrite the emphasized tags
  let newTagsIdsToEmphasize = tagIdsToEmphasize;
  if (state.currentCardId == cardId) {
    newTagsIdsToEmphasize = state.currentEventTagsIdToEmphasize
  }

  return {
    ...state,
    currentEventDataToDisplay: existingPageData ? existingPageData : null,
    currentEventIdDisplayed: eventId,
    currentEventShowAnswers: showAnswers,
    currentEventShowUser: showUsername,
    eventToDisplayIsLoading: existingPageData ? false : true,
    currentCardId: cardId,
    currentEventTagsIdToEmphasize: newTagsIdsToEmphasize
  };
};

const generateExampleReducer: StatsReducer<GenerateExamplePayload> = (state, { tagIds }) => {
  return {
    ...state,
    eventToDisplayIsLoading: true,
    currentEventTagsIdToEmphasize: (tagIds && tagIds.length > 0) ?
      tagIds.concat() : undefined
  }
};

const exampleToDisplayGeneratedReducer: StatsReducer<RawPageData> = (state, example) => {
  return {
    ...state,
    currentEventDataToDisplay: {...example},
    currentEventIdDisplayed: `${Date.now()}`,
    currentEventShowAnswers: false,
    currentEventShowUser: false,
    eventToDisplayIsLoading: false
  }
};

const removeDisplayedExampleReducer: StatsReducer<void> = (state) => {
  return {
    ...state,
    currentEventDataToDisplay: null,
    currentEventIdDisplayed: null,
    currentCardId: undefined,
    currentEventTagsIdToEmphasize: undefined
  }
};

const addNewEventReducer: StatsReducer<AddNewEventParams> = (state, { data, displayImmediately}) => {
  const adjustedState = addNewEventsReducer(state, [data]);
  const eventIdToShow = state.currentEventIdDisplayed;
  adjustedState.currentEventDataToDisplay = eventIdToShow ?
    adjustedState.eventIdToEvent[eventIdToShow] : null;
  if (displayImmediately)
    adjustedState.eventToDisplayIsLoading = false;
  return adjustedState;
};

const addNewEventsReducer: StatsReducer<StudentResult[]> = (state, newResults) => {
  const studentsStats = { ...state.studentsStats };
  const eventIdMap = state.eventIdToEvent;
  newResults.forEach(({ id, data }) => {
    if (!eventIdMap[id]) {
      const { type, skill } = data;

      // if the type is no longer supported, skip it
      if (isVestigialType(type)) return;

      // make sure we have a skill id in the result
      if (skill.id) {
        addNewStudentStatResult(studentsStats, data);
        state.skillIdsPresent[skill.id] = true;
        eventIdMap[id] = data;
      }
    }
  });
  return {
    ...state,
    studentsStats
  }
};

const setModuleCompletionReducer: StatsReducer<SetModuleCompletionPayload> = (state, {data, userId}) => {
  const copy = {
    ...state.competencyAndUserToCompletionMap
  };
  const key = `${data.moduleName} ${userId}`;
  copy[key] = data;
  return {
    ...state,
    competencyAndUserToCompletionMap: copy
  };
};

export const statisticsReducers = reducerWithInitialState(INITIAL_STATE)
  .cases([setCurrentCompetencyId, setStudentCurrentComptencyId], setCurrentCompetencyReducer)
  .case(addNewResults, addNewResultReducer)
  .case(startNewClassSession, sdCreatedReducer)
  .case(sdSetPollTimeout, sdSetPollTimeoutReducer)
  .case(updateRoster, handleUpdateRoster)
  .cases([setLTIModule, setURLModule], setLtiModuleReducer)
  .case(loadAndDisplayExampleFromEventId, loadAndDisplayExampleEventReducer)
  .case(generateExampleFromEventId, generateExampleReducer)
  .case(exampleToDisplayGenerated, exampleToDisplayGeneratedReducer)
  .case(removeDisplayedExample, removeDisplayedExampleReducer)
  .case(addNewEventForExample, addNewEventReducer)
  .case(addNewEvents, addNewEventsReducer)
  .case(setModuleCompletionForUser, setModuleCompletionReducer)
  .build();
